import { useCallback, useEffect, useRef, useState } from "react";
import {
  CheckCheck,
  Inbox,
  Loader2,
  Megaphone,
  Plus,
  Send,
  Trash2,
  UserRoundPlus,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/library/popover";
import { ScrollArea } from "@/components/ui/library/scroll-area";
import ConfirmDialog from "@/components/ui/ConfirmDialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  useAlertComposeOptions,
  useAlertInboxData,
  useDeleteAlert,
  useMarkAlertRead,
  useMarkAllAlertsRead,
  useSentAlerts,
} from "../api/useAlerts";
import { viewAlert } from "../announce/announceAlert";
import { alertTimeAgo, type AlertInboxItem, type AlertSentItem } from "../types";
import AlertComposeDialog from "./AlertComposeDialog";

type Tab = "inbox" | "sent";

/**
 * The header's Alert icon + box (owner, 2026-10-28). Sits beside the bell and
 * works the same way: badge with the unread count, a popover list, infinite
 * scroll. Two things of its own: the icon JIGGLES while anything is unread,
 * and admins/managers get "New alert" + a Sent tab (read counts, take back).
 */
export default function AlertDropdown() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("inbox");
  const [composeOpen, setComposeOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AlertSentItem | null>(null);
  const [optionsWanted, setOptionsWanted] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const inbox = useAlertInboxData();
  const options = useAlertComposeOptions(optionsWanted);
  const canCreate = Boolean(options.data?.canCreate);
  const sent = useSentAlerts(open && tab === "sent" && canCreate);

  const markRead = useMarkAlertRead();
  const markAllRead = useMarkAllAlertsRead();
  const deleteAlert = useDeleteAlert();

  // Compose options are cheap but only matter once the box is opened.
  useEffect(() => {
    if (open) setOptionsWanted(true);
  }, [open]);

  // Opening after the list has gone stale refetches it once; the socket
  // patches keep the rows faithful in between.
  useEffect(() => {
    if (open) void inbox.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const active = tab === "sent" && canCreate ? sent : inbox;
  const fetchNextPageRef = useRef(active.fetchNextPage);
  const hasNextPageRef = useRef(active.hasNextPage);
  const isFetchingNextPageRef = useRef(active.isFetchingNextPage);
  fetchNextPageRef.current = active.fetchNextPage;
  hasNextPageRef.current = active.hasNextPage;
  isFetchingNextPageRef.current = active.isFetchingNextPage;

  const handleScroll = useCallback((viewport: HTMLElement) => {
    if (!hasNextPageRef.current || isFetchingNextPageRef.current) return;
    if (viewport.scrollTop + viewport.clientHeight >= viewport.scrollHeight - 40) {
      void fetchNextPageRef.current();
    }
  }, []);

  // The Popover content mounts after `open` flips, so the viewport may not
  // exist on the first effect pass — poll briefly (same as the bell).
  useEffect(() => {
    if (!open) return;
    let viewport: HTMLElement | null = null;
    let listener: ((e: Event) => void) | null = null;
    let detached = false;
    const tryAttach = () => {
      if (detached) return;
      const v = scrollRef.current?.querySelector(
        "[data-slot='scroll-area-viewport']",
      ) as HTMLElement | null;
      if (!v) {
        requestAnimationFrame(tryAttach);
        return;
      }
      viewport = v;
      listener = () => handleScroll(v);
      v.addEventListener("scroll", listener);
    };
    tryAttach();
    return () => {
      detached = true;
      if (viewport && listener) viewport.removeEventListener("scroll", listener);
    };
  }, [open, tab, handleScroll]);

  const unreadCount = inbox.unreadCount;

  const openCompose = () => {
    setOpen(false);
    setComposeOpen(true);
  };

  // A row opens the announce popup. The box closes first: at z-[200] it would
  // otherwise sit on top of the dialog.
  const openAlert = (alert: AlertInboxItem | AlertSentItem) => {
    setOpen(false);
    viewAlert(alert);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl w-[1.5rem] h-[1.5rem] relative"
            aria-label={unreadCount > 0 ? `Alerts, ${unreadCount} unread` : "Alerts"}
            title="Alerts"
          >
            <Megaphone
              className={cn(
                "size-5 text-[var(--colorblack-10)]",
                unreadCount > 0 && "alert-icon--unread",
              )}
            />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 size-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center leading-none">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          sideOffset={8}
          className="w-[400px] p-0 rounded-xl shadow-xl border-[var(--color-black-1)] overflow-visible z-[200]"
        >
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-[var(--color-black-1)] rounded-t-xl bg-white">
            <div className="flex items-center gap-2">
              {/* The `!`: the global un-layered `h3 { font-size: 2.4rem }` beats plain utilities. */}
              <h3 className="text-[13px]! font-semibold text-[var(--color-black-9)] tracking-tight">
                Alerts
              </h3>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-[var(--color-primary)] text-white text-[9px] font-bold leading-none">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && tab === "inbox" && (
                <button
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] text-[var(--color-primary)] font-medium hover:bg-[var(--color-green-1)] transition-colors"
                  onClick={() => markAllRead.mutate()}
                >
                  <CheckCheck className="size-3" />
                  Mark all read
                </button>
              )}
              {canCreate && (
                <button
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] bg-[var(--color-primary)] text-white font-medium hover:opacity-90 transition-opacity"
                  onClick={openCompose}
                >
                  <Plus className="size-3" />
                  New alert
                </button>
              )}
            </div>
          </div>

          {canCreate && (
            <div className="flex items-center gap-1 px-3 py-1.5 border-b border-[var(--color-black-1)] bg-white">
              {(
                [
                  { key: "inbox", label: "Received", icon: Inbox },
                  { key: "sent", label: "Sent", icon: Send },
                ] as const
              ).map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={cn(
                    "inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-colors",
                    tab === t.key
                      ? "bg-[var(--color-green-1)] text-[var(--color-primary)]"
                      : "text-[var(--color-black-5)] hover:bg-[var(--color-black-1)]",
                  )}
                >
                  <t.icon className="size-3" />
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {/* Radix wraps the list in a `display: table` div that grows to the
              widest no-wrap line, which clipped the text, time and take-back
              button at the box's edge. Same fix as RightSideSheet. */}
          <ScrollArea
            ref={scrollRef}
            className="h-[380px] overflow-hidden rounded-b-xl bg-white [&_[data-slot=scroll-area-viewport]>div]:block! [&_[data-slot=scroll-area-viewport]>div]:w-full"
          >
            {tab === "sent" && canCreate ? (
              <SentList
                items={sent.sent}
                isLoading={sent.isLoading}
                isFetchingNextPage={sent.isFetchingNextPage}
                onDelete={(a) => setDeleteTarget(a)}
                onOpen={openAlert}
              />
            ) : (
              <InboxList
                items={inbox.alerts}
                isLoading={inbox.isLoading}
                isFetchingNextPage={inbox.isFetchingNextPage}
                onRead={(id) => markRead.mutate(id)}
                onOpen={openAlert}
              />
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>

      {options.data && (
        <AlertComposeDialog
          open={composeOpen}
          onOpenChange={setComposeOpen}
          options={options.data}
        />
      )}

      <ConfirmDialog
        open={deleteTarget != null}
        onClose={() => setDeleteTarget(null)}
        title="Take this alert back?"
        tone="destructive"
        warningTitle={deleteTarget?.title ?? ""}
        warningDescription={`It leaves the Alert box of all ${deleteTarget?.recipientCount ?? 0} people it went to.`}
        confirmLabel="Remove alert"
        loadingLabel="Removing…"
        isLoading={deleteAlert.isPending}
        size="sm"
        onConfirm={async () => {
          if (!deleteTarget) return;
          try {
            await deleteAlert.mutateAsync(deleteTarget.id);
            toast.success("Alert removed");
            setDeleteTarget(null);
          } catch {
            toast.error("Could not remove the alert");
          }
        }}
      />
    </>
  );
}

// ─── Lists ─────────────────────────────────────────────────

function EmptyState({ icon: Icon, text }: { icon: typeof Inbox; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full py-12 gap-2">
      <Icon className="size-8 text-[var(--color-black-3)]" />
      <p className="text-[12px] text-[var(--color-black-5)]">{text}</p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center h-full py-12 gap-2">
      <Loader2 className="size-6 text-[var(--color-black-3)] animate-spin" />
      <p className="text-[12px] text-[var(--color-black-5)]">Loading...</p>
    </div>
  );
}

function LoadingMore() {
  return (
    <div className="flex justify-center py-3">
      <span className="text-[10px] text-[var(--color-black-4)] animate-pulse">
        Loading more...
      </span>
    </div>
  );
}

function InboxList({
  items,
  isLoading,
  isFetchingNextPage,
  onRead,
  onOpen,
}: {
  items: AlertInboxItem[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  onRead: (id: number) => void;
  onOpen: (a: AlertInboxItem) => void;
}) {
  if (isLoading) return <LoadingState />;
  if (items.length === 0) return <EmptyState icon={Megaphone} text="No alerts yet" />;
  return (
    <>
      {items.map((a) => (
        <InboxRow key={a.id} alert={a} onRead={onRead} onOpen={onOpen} />
      ))}
      {isFetchingNextPage && <LoadingMore />}
    </>
  );
}

function InboxRow({
  alert,
  onRead,
  onOpen,
}: {
  alert: AlertInboxItem;
  onRead: (id: number) => void;
  onOpen: (a: AlertInboxItem) => void;
}) {
  const unread = !alert.readAt;
  const sender = alert.createdBy?.name?.trim() || "Admin";
  const announce = alert.mode === "ANNOUNCE";
  const request = alert.request ?? null;

  return (
    <button
      type="button"
      onClick={() => {
        if (unread) onRead(alert.id);
        onOpen(alert);
      }}
      className={cn(
        "w-full text-left flex gap-2.5 px-3 py-2.5 border-b border-[var(--color-black-1)] transition-colors hover:bg-[var(--color-black-1)]/60",
        unread && "bg-[var(--color-green-1)]/40",
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full",
          request
            ? "bg-amber-50 text-amber-600"
            : announce
              ? "bg-orange-50 text-orange-600"
              : "bg-[var(--color-green-1)] text-[var(--color-primary)]",
        )}
      >
        {request ? (
          <UserRoundPlus className="size-3.5" />
        ) : announce ? (
          <Megaphone className="size-3.5" />
        ) : (
          <Inbox className="size-3.5" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-start justify-between gap-2">
          <span
            className={cn(
              "text-[12.5px] leading-snug text-[var(--color-black-9)] break-words",
              unread ? "font-semibold" : "font-medium",
            )}
          >
            {alert.title}
          </span>
          <span className="shrink-0 text-[10px] text-[var(--color-black-4)] whitespace-nowrap pt-0.5">
            {alertTimeAgo(alert.createdAt)}
          </span>
        </span>
        <span className="mt-0.5 text-[11.5px] leading-relaxed text-[var(--color-black-7)] whitespace-pre-wrap break-words line-clamp-3">
          {alert.message}
        </span>
        <span className="mt-1 flex items-center gap-1.5 text-[10.5px] text-[var(--color-black-5)]">
          <span className="truncate">From {sender}</span>
          {request && <RequestChip status={request.status} resolution={request.resolution} />}
          {unread && <span className="size-1.5 rounded-full bg-[var(--color-primary)] shrink-0" />}
        </span>
      </span>
    </button>
  );
}

/** An assignment request's state on its row: Waiting / Done / Assigned / Cancelled. */
function RequestChip({ status, resolution }: { status: string; resolution: string | null }) {
  const label =
    status === "PENDING"
      ? "Waiting"
      : status === "CANCELLED"
        ? "Cancelled"
        : resolution === "assigned"
          ? "Assigned"
          : "Done";
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-1.5 py-px text-[9.5px] font-semibold",
        status === "PENDING"
          ? "bg-amber-50 text-amber-700"
          : status === "CANCELLED"
            ? "bg-[var(--color-black-1)] text-[var(--color-black-6)]"
            : "bg-[var(--color-green-1)] text-[var(--color-primary)]",
      )}
    >
      {label}
    </span>
  );
}

function SentList({
  items,
  isLoading,
  isFetchingNextPage,
  onDelete,
  onOpen,
}: {
  items: AlertSentItem[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  onDelete: (a: AlertSentItem) => void;
  onOpen: (a: AlertSentItem) => void;
}) {
  if (isLoading) return <LoadingState />;
  if (items.length === 0) return <EmptyState icon={Send} text="You haven't sent an alert yet" />;
  return (
    <>
      {items.map((a) => (
        <div
          key={a.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(a)}
          onKeyDown={(e) => {
            if (e.target !== e.currentTarget) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onOpen(a);
            }
          }}
          className="flex gap-2.5 px-3 py-2.5 border-b border-[var(--color-black-1)] cursor-pointer transition-colors hover:bg-[var(--color-black-1)]/60"
        >
          <span
            className={cn(
              "mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full",
              a.mode === "ANNOUNCE"
                ? "bg-orange-50 text-orange-600"
                : "bg-[var(--color-green-1)] text-[var(--color-primary)]",
            )}
          >
            {a.mode === "ANNOUNCE" ? <Megaphone className="size-3.5" /> : <Inbox className="size-3.5" />}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <span className="text-[12.5px] font-medium leading-snug text-[var(--color-black-9)] break-words">
                {a.title}
              </span>
              <span className="shrink-0 text-[10px] text-[var(--color-black-4)] whitespace-nowrap pt-0.5">
                {alertTimeAgo(a.createdAt)}
              </span>
            </div>
            <p className="m-0 mt-0.5 text-[11.5px] leading-relaxed text-[var(--color-black-7)] line-clamp-2 whitespace-pre-wrap break-words">
              {a.message}
            </p>
            <div className="mt-1 flex items-center justify-between gap-2 text-[10.5px] text-[var(--color-black-5)]">
              <span className="min-w-0 flex items-center gap-1">
                <Users className="size-3 shrink-0" />
                <span className="truncate">{a.audienceSummary || "—"}</span>
                <span className="shrink-0">
                  · {a.readCount}/{a.recipientCount} read
                </span>
                {a.request && <RequestChip status={a.request.status} resolution={a.request.resolution} />}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(a);
                }}
                className="shrink-0 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-red-500 hover:bg-red-50 transition-colors"
                title="Take this alert back"
              >
                <Trash2 className="size-3" />
              </button>
            </div>
          </div>
        </div>
      ))}
      {isFetchingNextPage && <LoadingMore />}
    </>
  );
}
